// Documents grouped by category (TDDS-3384). The backend returns, for `?grouped=true`:
// { categories: [{ key, label?, mandatory, expected, custom?, documents: [Doc] }], uncategorized: [Doc], canEdit }
// Standard categories come first; newer backends then add one `custom` group per Docrio tag that is not a
// category (key "TAG:<tag>", label = the tag), as in the BHP client portal.
// Older backends ignore the flag and return { files: [...] }; callers then keep the flat list.

export const DOCUMENT_CATEGORY_KEYS = [
  "ID_DOCUMENT",
  "POWER_OF_ATTORNEY",
  "APOC",
  "PHOTOGRAPHIC_EVIDENCE",
  "FEE_AGREEMENT",
  "PROOF_OF_ADDRESS",
  "OTHER",
];

// Categories the backend fills automatically (APOC = files whose name starts with "APOC"). They are never an
// upload/classify target (400) and their documents can't be moved (409 CATEGORY_LOCKED).
export const AUTO_ASSIGNED_CATEGORY_KEYS = ["APOC"];
export const isLockedCategory = (key) => AUTO_ASSIGNED_CATEGORY_KEYS.includes(key);
// Categories that are never "missing" unless the backend explicitly says they are expected.
const OPTIONAL_BY_DEFAULT = ["OTHER", ...AUTO_ASSIGNED_CATEGORY_KEYS];

// No longer a category (TDDS-3487): older backends may still send it; its files are shown under "Outros".
export const RETIRED_CATEGORY_KEYS = { SELF_DECLARATION_OF_LOSSES: "OTHER" };

// TDDS-3492: no banner pressing users for missing "important" documents for now.
// Flip to true once those documents become genuinely required.
export const PROMPT_FOR_MISSING_IMPORTANT_DOCUMENTS = false;

export const UPLOAD_ACCEPT =
  ".jpg,.jpeg,.pdf,.doc,.docx,.xls,.xlsx,application/msword,"
  + "application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,"
  + "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/jpeg,application/pdf";
export const UPLOAD_EXTENSIONS = ["jpg", "jpeg", "pdf", "doc", "docx", "xls", "xlsx"];
export const UPLOAD_MAX_BYTES = 20 * 1024 * 1024;

export const UNCATEGORIZED_PAGE_SIZE = 10;

export const isDocumentCategory = (key) => DOCUMENT_CATEGORY_KEYS.includes(key);

export const isTagGroup = (group) => Boolean(group?.custom) || String(group?.key || "").startsWith("TAG:");

export const categoryLabel = (key, t) =>
  (isDocumentCategory(key) ? t(`documents.categories.${key}`) : t("documents.uncategorized"));

export const isGroupedFilesResponse = (response) => Array.isArray(response?.categories);

const documentsOf = (group) => (Array.isArray(group?.documents) ? group.documents : []);

// Only the categories the backend returned, in the canonical order (TDDS-3407), followed by the
// Docrio tag groups that have documents. Works with responses with or without `custom`/`label`.
export const normalizeGroupedFiles = (response) => {
  const groups = (Array.isArray(response?.categories) ? response.categories : []).filter(Boolean);
  const byKey = new Map(groups.filter((group) => isDocumentCategory(group.key)).map((group) => [group.key, group]));
  const retired = groups.filter((group) => RETIRED_CATEGORY_KEYS[group.key]);
  retired.forEach((group) => {
    const target = RETIRED_CATEGORY_KEYS[group.key];
    if (!byKey.has(target) && documentsOf(group).length) byKey.set(target, { key: target, expected: false });
  });

  const categories = DOCUMENT_CATEGORY_KEYS.filter((key) => byKey.has(key)).map((key) => {
    const group = byKey.get(key);
    const moved = retired.filter((old) => RETIRED_CATEGORY_KEYS[old.key] === key).flatMap(documentsOf);
    return {
      key,
      mandatory: Boolean(group.mandatory),
      expected: group.expected ?? !OPTIONAL_BY_DEFAULT.includes(key),
      assignable: !isLockedCategory(key),
      documents: [...documentsOf(group), ...moved]
        .map((doc) => (isDocumentCategory(doc?.category) ? doc : { ...doc, category: key })),
    };
  });

  const tagGroups = groups
    .filter((group) => isTagGroup(group) && !isDocumentCategory(group.key) && documentsOf(group).length > 0)
    .map((group) => ({
      key: String(group.key),
      label: group.label || String(group.key).replace(/^TAG:/, ""),
      custom: true,
      documents: documentsOf(group).map((doc) => (doc?.category ? doc : { ...doc, category: group.key })),
    }));

  return {
    categories,
    tagGroups,
    uncategorized: Array.isArray(response?.uncategorized) ? response.uncategorized : [],
    canEdit: Boolean(response?.canEdit),
  };
};

export const missingMandatoryCategories = (data) =>
  (data?.categories || []).filter((group) => group.mandatory && group.documents.length === 0).map((group) => group.key);

// Moves a document into its new group; a blank category sends it to "a categorizar".
export const applyClassification = (data, updated) => {
  const without = (docs) => docs.filter((doc) => doc.id !== updated.id);
  const category = isDocumentCategory(updated.category) ? updated.category : null;
  return {
    ...data,
    uncategorized: category ? without(data.uncategorized) : [updated, ...without(data.uncategorized)],
    categories: data.categories.map((group) => ({
      ...group,
      documents: group.key === category ? [updated, ...without(group.documents)] : without(group.documents),
    })),
    tagGroups: (data.tagGroups || [])
      .map((group) => ({ ...group, documents: without(group.documents) }))
      .filter((group) => group.documents.length > 0),
  };
};

export const validateUploadFile = (file, t) => {
  if (file.size > UPLOAD_MAX_BYTES) return t("documents.errors.tooLarge").replace("{name}", file.name);
  const extension = (String(file.name).split(".").pop() || "").toLowerCase();
  if (!UPLOAD_EXTENSIONS.includes(extension)) return t("documents.errors.invalidType").replace("{name}", file.name);
  return "";
};

export const formatFileSize = (bytes) => {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size <= 0) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} MB`;
};

// Client-side placeholder ids (never sent to the download endpoint).
export const isMockDocumentId = (id) => String(id || "").startsWith("mock-");
